/**
 * The Intelligence axes — shared config for the sidebar group, the Overview cards and
 * each axis page's header.
 *
 * Lives outside any component so all three can import it (and so page files only export
 * components, which react-refresh requires).
 *
 * What belongs here is *standing* intelligence — durable facts about the adversary that
 * an analyst browses occasionally:
 *
 *   Botnets — whose sending/hosting network is this?
 *   Actors  — who operates it?
 *   Kits    — whose toolchain is this?  (global corpus; a tenant's own kits are
 *             detections, see below)
 *
 * One thing deliberately does NOT live here: Campaigns are the correlation layer that
 * combines every intel signal and drives takedowns — a top-level destination, not one of
 * several peers behind a menu.
 *
 * Note that kits reach an analyst two ways. A recovered kit is written as a `kit_hunter`
 * detection by kit_intel_tasks, so a tenant meets its *own* kits in the feed, filtered by
 * source; the axis here is the cross-tenant corpus, including everything unattributed.
 */
import { Network, Package, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ROUTES } from './constants';

export interface AxisTab {
  label: string;
  to: string;
  icon: LucideIcon;
  title: string;
  description: string;
}

export const BOTNETS_AXIS: AxisTab = {
  label: 'Botnets',
  to: ROUTES.BOTNETS,
  icon: Network,
  title: 'Botnets',
  description:
    'Attacker infrastructure — the sending and hosting networks behind the waves.',
};

export const ACTORS_AXIS: AxisTab = {
  label: 'Threat actors',
  to: ROUTES.ACTORS,
  icon: Users,
  title: 'Threat actors',
  description: 'Identity — hard identifiers and how far each one reaches.',
};

/** The global recovered-kit corpus. */
export const KITS_AXIS: AxisTab = {
  label: 'Phishing kits',
  to: ROUTES.KITS,
  icon: Package,
  title: 'Phishing kits',
  description:
    'Toolchain — the global recovered-kit corpus, correlated on metadata alone. Kits impersonating your brands also appear in Detections.',
};

/** The standing-intel axes, in the order an investigation usually walks them. */
export const INTELLIGENCE_AXES: AxisTab[] = [BOTNETS_AXIS, ACTORS_AXIS, KITS_AXIS];

/**
 * Paths belonging to the Intelligence surface, including the dossier routes that live at
 * top level because they are deep-linked from all over the app.
 *
 * `/campaigns` is deliberately absent: it has its own top-level sidebar entry and must
 * light on its own.
 */
const INTELLIGENCE_PREFIXES = ['/botnets', '/actors', '/kits'];

/** True when a path belongs to the Intelligence surface, index or dossier. */
export function isIntelligencePath(pathname: string): boolean {
  if (pathname === ROUTES.INTELLIGENCE || pathname.startsWith(`${ROUTES.INTELLIGENCE}/`)) {
    return true;
  }
  return INTELLIGENCE_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}
